import { Alert, Box, Button, Divider, Typography } from '@shared/ui';
import type React from 'react';
import CalendarRow from './CalendarRow';
import {
  type CalendarAccount,
  type CalendarProviderGroup,
  type CalendarSelectionErrors,
  type CalendarSelectionOverrides,
  calendarSelectionKey,
} from './types';

interface CalendarAccountSectionProps {
  account: CalendarAccount;
  provider: CalendarProviderGroup;
  mutationPending?: boolean;
  selectionOverrides: CalendarSelectionOverrides;
  selectionErrors: CalendarSelectionErrors;
  onReconnect: (provider: string, email?: string) => void;
  onDisconnectAccount: (args: {
    provider: string;
    connectionId: string;
    accountLabel: string;
  }) => void;
  onToggleCalendar: (args: {
    provider: string;
    connectionId: string;
    providerCalendarId: string;
    selected: boolean;
    previousSelected: boolean;
  }) => void;
}

const PROVIDER_ICON_URL: Record<string, string> = {
  'google-calendar':
    'https://ssl.gstatic.com/calendar/images/dynamiclogo_2020q4/calendar_20_2x.png',
  'microsoft-calendar':
    'https://res.cdn.office.net/files/fabric-cdn-prod_20230815.001/assets/brand-icons/product/svg/outlook_32x1.svg',
};
const PROVIDER_ICON_TEXT: Record<string, string> = {
  'icloud-calendar': '☁',
};
const ACCOUNT_CALENDAR_LIST_MAX_HEIGHT = 260;

const CalendarAccountSection: React.FC<CalendarAccountSectionProps> = ({
  account,
  provider,
  mutationPending = false,
  selectionOverrides,
  selectionErrors,
  onReconnect,
  onDisconnectAccount,
  onToggleCalendar,
}) => {
  const accountLabel =
    account.email || account.name || account.provider_user_id || 'Calendar account';
  const providerIcon = PROVIDER_ICON_URL[provider.provider];
  const providerIconText = PROVIDER_ICON_TEXT[provider.provider] ?? '📅';

  return (
    <Box data-testid="calendar-account-section" sx={{ width: '100%' }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          py: 1.75,
        }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
          {providerIcon ? (
            <Box
              component="img"
              alt=""
              src={providerIcon}
              sx={{ flexShrink: 0, height: 29, width: 29 }}
            />
          ) : (
            <Box
              aria-hidden="true"
              sx={{
                flexShrink: 0,
                fontSize: 22,
                height: 29,
                lineHeight: '29px',
                textAlign: 'center',
                width: 29,
              }}
            >
              {providerIconText}
            </Box>
          )}
          <Typography
            sx={{
              color: '#000000',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.15px',
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {provider.display_name} ({accountLabel})
          </Typography>
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', flexShrink: 0, gap: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={() =>
              onDisconnectAccount({
                provider: provider.provider,
                connectionId: account.connection_id,
                accountLabel,
              })
            }
            sx={{ color: '#505A54', fontSize: 12, minWidth: 0, px: 1 }}
          >
            Disconnect
          </Button>
          {account.error?.reconnect_required ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => onReconnect(provider.provider, account.email)}
            >
              Reconnect
            </Button>
          ) : null}
        </Box>
      </Box>

      {account.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {account.error.message}
        </Alert>
      ) : null}

      <Box
        data-testid="calendar-account-calendar-list"
        sx={{
          maxHeight: ACCOUNT_CALENDAR_LIST_MAX_HEIGHT,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          pr: 1,
          scrollbarGutter: 'stable',
        }}
      >
        {account.calendars.length === 0 && !account.error ? (
          <Typography sx={{ color: '#A4A4A4', fontSize: 12, py: 1 }}>
            No calendars returned for this account.
          </Typography>
        ) : (
          account.calendars.map((calendar) => {
            const selectionKey = calendarSelectionKey(
              provider.provider,
              account.connection_id,
              calendar.provider_calendar_id
            );
            const effectiveSelected =
              selectionOverrides[selectionKey] ?? calendar.selected_for_conflicts;
            return (
              <CalendarRow
                key={calendar.provider_calendar_id}
                calendar={{ ...calendar, selected_for_conflicts: effectiveSelected }}
                errorFlash={selectionErrors[selectionKey]}
                disabled={
                  mutationPending ||
                  !(provider.capabilities.can_read_busy || provider.capabilities.can_read_events)
                }
                onToggle={(selected) =>
                  onToggleCalendar({
                    provider: provider.provider,
                    connectionId: account.connection_id,
                    providerCalendarId: calendar.provider_calendar_id,
                    selected,
                    previousSelected: effectiveSelected,
                  })
                }
              />
            );
          })
        )}
      </Box>
      <Divider sx={{ borderColor: '#E3E3E3', my: 3 }} />
    </Box>
  );
};

export default CalendarAccountSection;
